/*
 * Copyright (c) 2019 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import dayjs from 'dayjs';
import Component from 'can/component/component';
import FeedService from 'scripts/services/base-feed-service';
import template from './viewing-schedule-table.stache';
import dateTimeUtil from 'scripts/utils/date-time';
import 'dotdotdot';

const tvScheduleWithDateUrl =
    window.globalSettings.feeds.schedule.tvScheduleWithDateUrl;
const livesScheduleByDayUrl =
    window.globalSettings.feeds.schedule.viewingScheduleLivesWithDateUrl;

let previousLiveCards = null;

const timeFormatWithMeridiem = 'h:mm A';

const getDateStr = (referenceDateStr, timeStr) => {
    const timeObject = dayjs(timeStr, timeFormatWithMeridiem, true);

    if (timeObject.isValid()) {
        const date = dayjs.parseZone(referenceDateStr).set({
            hour: timeObject.get('hour'),
            minutes: timeObject.get('minute'),
        });

        return date.format();
    }

    return timeStr;
};
const getMinimumDate = (dates) => {
    const filteredDates = dates.filter((date) => date !== undefined);
    if (filteredDates.length < 1) {
        return null;
    }
    return dayjs.min(filteredDates);
};
const getMaximumDate = (dates) => {
    const filteredDates = dates.filter((date) => date !== undefined);
    if (filteredDates.length < 1) {
        return null;
    }
    return dayjs.max(filteredDates);
};
const fixEventCardDates = (card, eventCard) => {
    eventCard.startDate = getDateStr(card.startDate, eventCard.startDate);
    eventCard.endDate = getDateStr(card.endDate, eventCard.endDate);
    return eventCard;
};
const sortEventCardsByStartTime = (card1, card2) => {
    return dateTimeUtil.timeDiffPreciseInHours(
        card2.startDate,
        card1.startDate,
    );
};

function* generateTimeTitles(startDate, endDate, step) {
    const doTimeFormat = (time) => dayjs(time).format(timeFormatWithMeridiem);
    let tempDate = dateTimeUtil.copyDate(startDate);
    do {
        yield doTimeFormat(tempDate);
        dateTimeUtil.addHours(tempDate, step);
    } while (dateTimeUtil.timeDiffPreciseInHours(tempDate, endDate) > 0);

    yield doTimeFormat(tempDate);
}

const dotdotdotSelector =
    '.c-viewing-schedule-table__row--data .c-viewing-schedule-table__row--wrap';
const truncatedClass = 'c-viewing-schedule-table__cell--truncated';
const cellSelector = '.c-viewing-schedule-table__cell';

Component.extend({
    tag: 'viewing-schedule-table',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            timelineTotalHours: {
                type: 'number',
                value: 0,
            },
            scheduleDate: {
                type: 'string',
                value: null,
            },
            broadcasts: {
                Type: can.List,
                Value: can.List,
            },
            liveVideoCards: {
                Type: can.List,
                Value: can.List,
            },
            broadcastsByChannel: {
                get() {
                    return _.reduce(
                        this.attr('broadcasts'),
                        (accum, broadcast) => {
                            if (!accum[broadcast.channelProvider]) {
                                accum[broadcast.channelProvider] = [];
                            }
                            accum[broadcast.channelProvider].push(broadcast);
                            return accum;
                        },
                        {},
                    );
                },
            },
            eventsTimeTitles: {
                Type: can.List,
                Value: can.List,
            },
        },
        getTimeTitles(startDate, endDate, hourStep) {
            if (!startDate && !hourStep) {
                return [];
            }
            const broadcastsEventsLength = this.attr('broadcasts').length;
            const totalEventsCount = _.reduce(
                this.attr('liveVideoCards'),
                (acc, cur) => {
                    return acc + cur.events.length;
                },
                broadcastsEventsLength,
            );
            if (totalEventsCount < 2) {
                return [];
            }

            return [...generateTimeTitles(startDate, endDate, hourStep)];
        },
        getCardsBoundaryDates(cards) {
            const startDates = cards
                .map((card) => dayjs(card.startDate))
                .serialize();
            const endDates = cards
                .map((card) => dayjs(card.endDate))
                .serialize();
            const cardsMinDate = getMinimumDate(startDates);
            const cardsMaxDate = getMaximumDate(endDates);

            return {
                minDate: cardsMinDate,
                maxDate: cardsMaxDate,
                isValid: cardsMinDate && cardsMaxDate,
            };
        },
        updatedBroadcasts() {
            const broadcastsBoundary = this.getCardsBoundaryDates(
                this.attr('broadcasts'),
            );
            if (broadcastsBoundary.isValid) {
                this.attr('minDateBroadcasts', broadcastsBoundary.minDate);
                this.attr('maxDateBroadcasts', broadcastsBoundary.maxDate);
                this.updateMainTableData();
            }
        },
        updatedLives() {
            const livesBoundary = this.getCardsBoundaryDates(
                this.attr('liveVideoCards'),
            );
            if (livesBoundary.isValid) {
                this.attr('minDateLives', livesBoundary.minDate);
                this.attr('maxDateLives', livesBoundary.maxDate);
                this.updateMainTableData();
            }
        },
        updateMainTableData() {
            const roundedDates = this.calculateRoundedDates();

            this.attr('minDateRoundedDown', roundedDates.minDate);
            this.attr('maxDateRoundedUp', roundedDates.maxDate);
            this.attr('roundedEventsDuration', roundedDates.duration);

            const eventsTimeStepInHours = this.getEventsTimeStepInHours(
                roundedDates.duration,
            );
            this.attr('eventsTimeStepInHours', eventsTimeStepInHours);
            const timeTitles = this.getTimeTitles(
                roundedDates.minDate,
                roundedDates.maxDate,
                eventsTimeStepInHours,
            );
            this.attr('eventsTimeTitles', timeTitles);
            const timelineTotalHours = this.countTimelineTotalLength(
                timeTitles,
                eventsTimeStepInHours,
            );
            this.attr('timelineTotalHours', timelineTotalHours);
        },
        calculateRoundedDates() {
            const minDateAll = getMinimumDate([
                this.attr('minDateBroadcasts'),
                this.attr('minDateLives'),
            ]).toDate();
            const maxDateAll = getMaximumDate([
                this.attr('maxDateBroadcasts'),
                this.attr('maxDateLives'),
            ]).toDate();

            const minDateRoundedDown = this.getMinDateRoundedDown(minDateAll);
            const maxDateRoundedUp = this.getMaxDateRoundedUp(maxDateAll);
            const roundedEventsDuration = dateTimeUtil.timeDiffPreciseInHours(
                minDateRoundedDown,
                maxDateRoundedUp,
            );

            return {
                minDate: minDateRoundedDown,
                maxDate: maxDateRoundedUp,
                duration: roundedEventsDuration,
            };
        },
        getMinDateRoundedDown(minDate) {
            minDate.setMinutes(0);
            return minDate;
        },
        getMaxDateRoundedUp(maxDate) {
            let hours = maxDate.getHours();
            const minutes = maxDate.getMinutes();
            if (minutes > 30) {
                maxDate.setHours(hours + 1);
                maxDate.setMinutes(0);
            } else if (minutes > 0 && minutes < 30) {
                maxDate.setMinutes(30);
            }
            return maxDate;
        },
        getEventsTimeStepInHours(roundedEventsDuration) {
            if (roundedEventsDuration <= 3) {
                return 0.5;
            }
            if (roundedEventsDuration > 7) {
                return 2;
            }
            return 1;
        },
        countTimelineTotalLength(timeTitles, step) {
            const segmentCount = timeTitles.length - 1;
            return segmentCount * step;
        },
    },
    events: {
        inserted() {
            this.initLiveFeedService();
            this.initScheduleTVFeedService();
            this.viewModel.bind('scheduleDate', () => {
                this.destroyLiveFeedService();
                this.initScheduleTVFeedService();
                this.initLiveFeedService();
            });
        },

        removed() {
            this.destroyLiveFeedService();
        },

        initScheduleTVFeedService() {
            const scheduleDate = this.viewModel.attr('scheduleDate');
            const scheduleTVfeedUrl = can.sub(tvScheduleWithDateUrl, {
                date: scheduleDate,
            });
            new FeedService({
                feedUrl: scheduleTVfeedUrl,
                fromCache: false,
                autoUpdate: false,
            })
                .on('data', (type, data) => {
                    const sortedBroadcasts = data.broadcasts.sort(
                        sortEventCardsByStartTime,
                    );
                    this.viewModel.attr('broadcasts', sortedBroadcasts);
                    this.viewModel.updatedBroadcasts();
                    this.showEllipses();
                })
                .on('error', (error) => {
                    console.error('Error fetching tv schedule data', error);
                })
                .fetch({ date: scheduleDate });
        },

        initLiveFeedService() {
            const scheduleDate = this.viewModel.attr('scheduleDate');
            const feedUrl = can.sub(livesScheduleByDayUrl, {
                date: scheduleDate,
            });
            this.liveFeedService = new FeedService({
                feedUrl,
                fromCache: false,
                autoUpdate: true,
            });
            this.liveFeedService
                .on('data', (ev, data) => {
                    const correctedCards = data.cards.map((card) => {
                        card.events = card.events
                            .map((eventCard) =>
                                fixEventCardDates(card, eventCard),
                            )
                            .sort(sortEventCardsByStartTime);
                        return card;
                    });
                    // check if the cards need an update
                    if (_.isEqual(previousLiveCards, correctedCards)) {
                        return;
                    }
                    previousLiveCards = _.cloneDeep(correctedCards);

                    can.batch.start();
                    let liveVideoCards = this.viewModel.attr('liveVideoCards');
                    // add new elements
                    correctedCards
                        .filter((card) => {
                            let isNew = true;
                            liveVideoCards.each((liveCard) => {
                                if (
                                    card.channelIdentifier ===
                                    liveCard.channelIdentifier
                                ) {
                                    can.Map.keys(liveCard).forEach((key) => {
                                        liveCard.attr(key, card[key]);
                                    });
                                    isNew = false;
                                }
                            });
                            return isNew;
                        })
                        .forEach((card) => {
                            liveVideoCards.push(card);
                        });
                    // clean old cards
                    liveVideoCards
                        .filter((liveCard) => {
                            let remove = true;
                            correctedCards.forEach((card) => {
                                if (
                                    remove &&
                                    card.channelIdentifier ===
                                        liveCard.channelIdentifier
                                ) {
                                    remove = false;
                                }
                            });
                            return remove;
                        })
                        .forEach((liveCard) => {
                            liveVideoCards.splice(
                                liveVideoCards.indexOf(liveCard),
                                1,
                            );
                        });
                    can.batch.stop();
                    this.viewModel.updatedLives();
                    this.showEllipses();
                })
                .on('error', (error) => {
                    console.error('Error fetching live schedule data', error);
                });
            this.liveFeedService.fetch();
        },

        destroyLiveFeedService() {
            if (this.liveFeedService) {
                this.liveFeedService.destroy();
                this.liveFeedService = null;
            }
        },

        showEllipses() {
            this.element.find(dotdotdotSelector).dotdotdot({
                callback: this.dotdotdotCallback,
            });
        },

        dotdotdotCallback(isTruncated) {
            if (isTruncated) {
                can.$(this)
                    .closest(cellSelector)
                    .addClass(truncatedClass);
            } else {
                can.$(this)
                    .closest(cellSelector)
                    .removeClass(truncatedClass);
            }
        },
    },
});
